import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Paper, Stack, Button, Card, CardContent,
    Stepper, Step, StepLabel, Avatar, Chip, LinearProgress, Fade, Zoom,
    Accordion, AccordionSummary, AccordionDetails, Divider,
    IconButton, Dialog, DialogTitle, DialogContent, useTheme, useMediaQuery
} from '@mui/material';
import {
    Psychology, Recycling, EmojiEvents,
    PhotoCamera, Speed, TrendingUp, CheckCircle, PlayArrow,
    ExpandMore, Close, CameraAlt, SmartToy, Timeline,
    Security, Language, PhoneAndroid, Analytics, Public
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import ImpactStats from '../components/layout/Footer/ImpactStats';

const HowItWorksPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [activeStep, setActiveStep] = useState(0);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedDemo, setSelectedDemo] = useState(null);
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setAnimate(true), 300);
        return () => clearTimeout(timer);
    }, []);

    const steps = [
        {
            icon: <PhotoCamera />,
            title: "📸 Capture or Upload",
            description: "Take a photo or upload an image of your waste item",
            details: "Use your device camera or select from gallery. Our AI supports all common image formats.",
            color: "#43a047"
        },
        {
            icon: <Psychology />,
            title: "🧠 AI Analysis",
            description: "Our advanced AI analyzes your waste in real-time",
            details: "State-of-the-art machine learning model trained on thousands of waste images.",
            color: "#00b894"
        },
        {
            icon: <Recycling />,
            title: "♻️ Get Classification",
            description: "Receive instant classification with disposal instructions",
            details: "Get category, confidence level, and eco-friendly disposal recommendations.",
            color: "#ffc400"
        },
        {
            icon: <EmojiEvents />,
            title: "🏆 Earn Rewards",
            description: "Gain champion points and climb the leaderboard",
            details: "Build your eco-warrior profile and compete with friends for sustainability.",
            color: "#e53935"
        }
    ];

    const detailedSteps = [
        {
            icon: <CameraAlt />,
            title: "📸 Capture Your Waste",
            subtitle: "Multiple Input Methods",
            description: "Take a photo or upload an image of any waste item you want to classify",
            details: [
                "📱 Use device camera for instant capture",
                "📁 Upload from gallery or file system",
                "🖼️ Supports JPG, PNG, WebP formats",
                "📏 Maximum 5MB file size",
                "💡 Best results with clear, well-lit photos"
            ],
            techSpecs: "Real-time image preprocessing with automatic enhancement",
            color: "#43a047",
            demoType: "camera"
        },
        {
            icon: <SmartToy />,
            title: "🧠 AI-Powered Analysis",
            subtitle: "Advanced Machine Learning",
            description: "Our state-of-the-art AI model analyzes your waste in milliseconds",
            details: [
                "🔬 Deep learning neural networks",
                "🎯 Trained on 100,000+ waste images",
                "🇧🇩 Specialized for Bangladesh waste types",
                "⚡ Sub-2-second processing time",
                "📊 90%+ classification accuracy"
            ],
            techSpecs: "Convolutional Neural Network with transfer learning architecture",
            color: "#9c27b0",
            demoType: "ai"
        },
        {
            icon: <Analytics />,
            title: "📋 Smart Classification",
            subtitle: "Detailed Results & Recommendations",
            description: "Get comprehensive classification with disposal instructions",
            details: [
                "🏷️ Precise waste category identification",
                "📈 Confidence percentage score",
                "♻️ Eco-friendly disposal instructions",
                "🌍 Environmental impact information",
                "📚 Educational tips and best practices"
            ],
            techSpecs: "Real-time result processing with localized recommendations",
            color: "#ff9800",
            demoType: "results"
        },
        {
            icon: <TrendingUp />,
            title: "🏆 Track & Improve",
            subtitle: "Gamification & Progress",
            description: "Earn champion points and track your environmental impact",
            details: [
                "🎯 Champion points for every classification",
                "📊 Personal impact dashboard",
                "🏅 Achievement badges and levels",
                "🥇 Community leaderboards",
                "📈 Environmental contribution tracking"
            ],
            techSpecs: "Comprehensive analytics and gamification system",
            color: "#4caf50",
            demoType: "rewards"
        }
    ];

    const features = [
        {
            icon: <Speed />,
            title: "Lightning Fast",
            description: "Get results in under 2 seconds",
            stat: "< 2s",
            color: "#ff5722"
        },
        {
            icon: <Analytics />,
            title: "Highly Accurate",
            description: "90%+ classification accuracy",
            stat: "90%+",
            color: "#4caf50"
        },
        {
            icon: <Language />,
            title: "Bangladesh Focused",
            description: "Trained on local waste types",
            stat: "100%",
            color: "#2196f3"
        },
        {
            icon: <Security />,
            title: "Privacy First",
            description: "Your data stays secure",
            stat: "🔒",
            color: "#9c27b0"
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)' }}>
            {/* Hero Section - Mobile Responsive */}
            <Box sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                py: { xs: 4, sm: 6, md: 8 }
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="center">
                        <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                                        lineHeight: { xs: 1.2, sm: 1.3 }
                                    }}
                                >
                                    ⚙️ How Sortify Works
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        opacity: 0.9,
                                        mb: { xs: 3, sm: 4 },
                                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                                        lineHeight: { xs: 1.3, sm: 1.4 },
                                        px: { xs: 1, sm: 0 }
                                    }}
                                >
                                    Discover how our AI-powered waste classification system makes environmental responsibility simple and rewarding
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1, sm: 2 }}
                                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                                    justifyContent={{ xs: 'center', md: 'flex-start' }}
                                >
                                    <Chip
                                        label="🤖 AI-Powered"
                                        sx={{
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    />
                                    <Chip
                                        label="⚡ Lightning Fast"
                                        sx={{
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    />
                                    <Chip
                                        label="🎯 90%+ Accurate"
                                        sx={{
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
                            <Box sx={{
                                textAlign: 'center',
                                fontSize: { xs: '4rem', sm: '6rem', md: '8rem' }
                            }}>
                                🔄
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Quick Steps Overview - Mobile Responsive */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
                <Typography
                    variant="h3"
                    textAlign="center"
                    fontWeight={700}
                    gutterBottom
                    color="#1b5e20"
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        mb: { xs: 4, sm: 6 }
                    }}
                >
                    🚀 Simple 4-Step Process
                </Typography>

                {/* Mobile-First Stepper */}
                {isMobile ? (
                    // Mobile: Vertical Cards Layout
                    <Stack spacing={{ xs: 3, sm: 4 }}>
                        {steps.map((step, index) => (
                            <Card
                                key={index}
                                sx={{
                                    borderRadius: { xs: 3, sm: 4 },
                                    border: `3px solid ${step.color}20`,
                                    transition: 'all 0.3s ease',
                                    background: `linear-gradient(135deg, ${step.color}05, #ffffff)`
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: step.color,
                                                width: { xs: 50, sm: 60 },
                                                height: { xs: 50, sm: 60 },
                                                fontSize: { xs: '1.2rem', sm: '1.5rem' }
                                            }}
                                        >
                                            {index + 1}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="h6"
                                                fontWeight={700}
                                                gutterBottom
                                                sx={{
                                                    color: step.color,
                                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                                }}
                                            >
                                                {step.title}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="text.secondary"
                                                paragraph
                                                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                            >
                                                {step.description}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontStyle: 'italic',
                                                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                                                }}
                                            >
                                                {step.details}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                ) : (
                    // Desktop: Horizontal Stepper
                    <Stepper
                        activeStep={activeStep}
                        alternativeLabel
                        sx={{ mb: 6 }}
                    >
                        {steps.map((step, index) => (
                            <Step key={index}>
                                <StepLabel
                                    StepIconComponent={() => (
                                        <Avatar
                                            sx={{
                                                bgcolor: step.color,
                                                width: 60,
                                                height: 60,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setActiveStep(index)}
                                        >
                                            {step.icon}
                                        </Avatar>
                                    )}
                                >
                                    <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>
                                        {step.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {step.description}
                                    </Typography>
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                )}

                {/* CTA Button */}
                <Box sx={{ textAlign: 'center', mt: { xs: 4, sm: 6 } }}>
                    <Button
                        component={Link}
                        to="/classify"
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrow />}
                        sx={{
                            fontWeight: 700,
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1.2, sm: 1.5 },
                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                            background: 'linear-gradient(45deg, #43a047, #66bb6a)',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #388e3c, #43a047)'
                            }
                        }}
                    >
                        {isSmallMobile ? 'Try Now' : 'Try Sortify Now'}
                    </Button>
                </Box>
            </Container>

            {/* Features Grid - Mobile Responsive */}
            <Box sx={{ background: 'linear-gradient(135deg, #ffffff, #f5fbf7)', py: { xs: 6, sm: 8 } }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        textAlign="center"
                        fontWeight={700}
                        gutterBottom
                        color="#1b5e20"
                        sx={{
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                            mb: { xs: 4, sm: 6 }
                        }}
                    >
                        ✨ Why Choose Sortify?
                    </Typography>

                    <Grid container spacing={{ xs: 3, sm: 4 }}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card sx={{
                                    height: '100%',
                                    textAlign: 'center',
                                    borderRadius: { xs: 3, sm: 4 },
                                    border: `2px solid ${feature.color}20`,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: { xs: 'none', sm: 'translateY(-8px)' },
                                        boxShadow: `0 16px 48px ${feature.color}20`,
                                        borderColor: feature.color
                                    }
                                }}>
                                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                        <Avatar sx={{
                                            bgcolor: feature.color,
                                            width: { xs: 60, sm: 70 },
                                            height: { xs: 60, sm: 70 },
                                            mx: 'auto',
                                            mb: { xs: 2, sm: 3 }
                                        }}>
                                            {feature.icon}
                                        </Avatar>
                                        <Typography
                                            variant="h4"
                                            fontWeight={700}
                                            color={feature.color}
                                            gutterBottom
                                            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                                        >
                                            {feature.stat}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight={600}
                                            gutterBottom
                                            sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                                        >
                                            {feature.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Detailed Process - Mobile Responsive */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
                <Typography
                    variant="h3"
                    textAlign="center"
                    fontWeight={700}
                    gutterBottom
                    color="#1b5e20"
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        mb: { xs: 4, sm: 6 }
                    }}
                >
                    🔍 Deep Dive: How It Really Works
                </Typography>

                <Stack spacing={{ xs: 3, sm: 4 }}>
                    {detailedSteps.map((step, index) => (
                        <Accordion
                            key={index}
                            sx={{
                                borderRadius: { xs: 3, sm: 4 },
                                border: `2px solid ${step.color}20`,
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': {
                                    borderColor: step.color,
                                    boxShadow: `0 8px 32px ${step.color}15`
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore sx={{ color: step.color }} />}
                                sx={{
                                    py: { xs: 1, sm: 1.5 },
                                    px: { xs: 2, sm: 3 },
                                    '&.Mui-expanded': {
                                        backgroundColor: `${step.color}05`
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Avatar
                                        sx={{
                                            bgcolor: step.color,
                                            width: { xs: 50, sm: 60 },
                                            height: { xs: 50, sm: 60 },
                                            mr: { xs: 2, sm: 3 }
                                        }}
                                    >
                                        {step.icon}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h5"
                                            fontWeight={700}
                                            color={step.color}
                                            sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            fontWeight={600}
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                            {step.subtitle}
                                        </Typography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                                <Box sx={{ ml: { xs: 7, sm: 9 } }}>
                                    <Typography
                                        variant="body1"
                                        paragraph
                                        sx={{
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            mb: 2
                                        }}
                                    >
                                        {step.description}
                                    </Typography>

                                    <Stack spacing={1} sx={{ mb: 3 }}>
                                        {step.details.map((detail, i) => (
                                            <Typography
                                                key={i}
                                                variant="body2"
                                                sx={{
                                                    color: '#546e7a',
                                                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                                                }}
                                            >
                                                {detail}
                                            </Typography>
                                        ))}
                                    </Stack>

                                    <Paper
                                        sx={{
                                            p: { xs: 2, sm: 2.5 },
                                            backgroundColor: `${step.color}05`,
                                            border: `1px solid ${step.color}20`,
                                            borderRadius: 2
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
                                        >
                                            💻 TECHNICAL SPECS
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.5,
                                                fontStyle: 'italic',
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                        >
                                            {step.techSpecs}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Stack>
            </Container>

            {/* Environmental Impact Stats */}
            <ImpactStats variant="section" />

            {/* Final CTA - Mobile Responsive */}
            <Box sx={{
                background: 'linear-gradient(135deg, #1b5e20, #43a047)',
                color: 'white',
                py: { xs: 4, sm: 6 },
                textAlign: 'center'
            }}>
                <Container maxWidth="md">
                    <Typography
                        variant="h3"
                        fontWeight={800}
                        gutterBottom
                        sx={{
                            fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.5rem' },
                            lineHeight: { xs: 1.2, sm: 1.3 }
                        }}
                    >
                        Ready to Start Your Eco-Journey? 🌱
                    </Typography>
                    <Typography
                        variant="h6"
                        paragraph
                        sx={{
                            opacity: 0.9,
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                            px: { xs: 2, sm: 0 }
                        }}
                    >
                        Join thousands of Bangladeshi users making a positive environmental impact
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="center"
                        sx={{ mt: { xs: 3, sm: 4 } }}
                    >
                        <Button
                            component={Link}
                            to="/classify"
                            variant="contained"
                            size="large"
                            startIcon={<PhotoCamera />}
                            sx={{
                                background: 'linear-gradient(135deg, #ffc400, #ffea00)',
                                color: '#1b5e20',
                                fontWeight: 700,
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.2, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #ffb300, #ffc400)'
                                }
                            }}
                        >
                            {isSmallMobile ? 'Classify Now' : 'Start Classifying Waste'}
                        </Button>
                        <Button
                            component={Link}
                            to="/about"
                            variant="outlined"
                            size="large"
                            sx={{
                                borderColor: 'white',
                                color: 'white',
                                fontWeight: 700,
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.2, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            Learn More
                        </Button>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default HowItWorksPage;
